import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialIcons } from '@expo/vector-icons';

const appIcon = require('../assets/icon.png');

// localStorage key for dismiss state
const DISMISS_STORAGE_KEY = 'pwa_install_dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * PWA Install Button Component
 * Kullanıcılara uygulamayı ana ekrana ekleme seçeneği sunar
 * Android/Chrome için beforeinstallprompt event'ini kullanır
 * iOS için manuel talimat gösterir
 */
const InstallPWAButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showButton, setShowButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isDismissed, setIsDismissed] = useState(true); // Default: hidden, will check storage

  useEffect(() => {
    // Sadece web platformunda çalış
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      setIsIOS(isIOSDevice);

      // Check if user previously dismissed the prompt
      const checkDismissed = () => {
        try {
          const dismissed = localStorage.getItem(DISMISS_STORAGE_KEY);
          if (dismissed === 'true') {
            setIsDismissed(true);
            return true;
          }
          setIsDismissed(false);
          return false;
        } catch (error) {
          console.warn('Failed to check dismiss state:', error);
          setIsDismissed(false);
          return false;
        }
      };

      // Zaten yüklü mü kontrol et (standalone mode)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setShowButton(false);
        setIsDismissed(true);
        return;
      }

      // Check dismiss state first
      const wasDismissed = checkDismissed();
      if (wasDismissed) {
        return; // Don't show if previously dismissed
      }

      // beforeinstallprompt event'i yakala (Android/Chrome)
      const handleBeforeInstallPrompt = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setShowButton(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(DISMISS_STORAGE_KEY, 'true');
      }
    } catch (error) {
      console.warn('Failed to save dismiss state:', error);
    }
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('Kullanıcı PWA yüklemeyi kabul etti');
        setShowButton(false);
        handleDismiss(); // Don't show again after install
      } else {
        console.log('Kullanıcı PWA yüklemeyi reddetti');
        handleDismiss(); // Don't show again after rejection
      }
    } catch (error) {
      console.error('PWA yükleme hatası:', error);
    }

    setDeferredPrompt(null);
  };

  // iOS için özel mesaj göster
  if (isIOS && Platform.OS === 'web' && !isDismissed) {
    return (
      <View style={styles.container}>
        <LinearGradient colors={['#1b2436', '#0a0f1a']} style={styles.iosContainer}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleDismiss}
            accessibilityRole="button"
            accessibilityLabel="Close install prompt"
          >
            <MaterialIcons name="close" size={20} color="rgba(255,255,255,0.7)" />
          </TouchableOpacity>
          <View style={styles.iosHeader}>
            <Image source={appIcon} style={styles.iosIcon} />
            <View style={styles.iosHeaderText}>
              <Text style={styles.iosTitle}>VerifyGate</Text>
              <Text style={styles.iosSubtitle}>Ana Ekrana Ekle</Text>
            </View>
          </View>
          <View style={styles.iosStepsWrapper}>
            <View style={styles.iosStepRow}>
              <View style={styles.iosStepIcon}>
                <MaterialIcons name="share" size={20} color="#46a6ff" />
              </View>
              <Text style={styles.iosStep}>
                <Text style={styles.iosStepNumber}>1.</Text> Paylaş butonuna dokunun
              </Text>
            </View>
            <View style={styles.iosStepRow}>
              <View style={styles.iosStepIcon}>
                <MaterialIcons name="add-circle-outline" size={20} color="#46a6ff" />
              </View>
              <Text style={styles.iosStep}>
                <Text style={styles.iosStepNumber}>2.</Text> “Ana Ekrana Ekle” seçeneğini seçin
              </Text>
            </View>
          </View>
        </LinearGradient>
      </View>
    );
  }

  // Android/Chrome için buton göster
  if (!showButton || !deferredPrompt || isDismissed) {
    return null;
  }

  return (
    <View style={styles.container}>
      <LinearGradient colors={['#137fec', '#0d51ad']} style={styles.buttonWrapper}>
        <TouchableOpacity
          style={styles.closeButtonAndroid}
          onPress={handleDismiss}
          accessibilityRole="button"
          accessibilityLabel="Close install prompt"
        >
          <MaterialIcons name="close" size={18} color="rgba(255,255,255,0.8)" />
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.button}
          onPress={handleInstallClick}
          activeOpacity={0.85}
        >
          <View style={styles.buttonContent}>
            <Image source={appIcon} style={styles.buttonIcon} />
            <View>
              <Text style={styles.buttonText}>Ana Ekrana Ekle</Text>
              <Text style={styles.buttonSubText}>VerifyGate'i hızlı başlat</Text>
            </View>
          </View>
        </TouchableOpacity>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    zIndex: 1000,
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  buttonWrapper: {
    borderRadius: 28,
    padding: 2,
    width: '100%',
    maxWidth: 360,
  },
  button: {
    backgroundColor: 'rgba(17, 24, 39, 0.9)',
    paddingVertical: 14,
    paddingHorizontal: 18,
    borderRadius: 26,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  buttonIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.4,
  },
  buttonSubText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    fontWeight: '500',
  },
  iosContainer: {
    borderRadius: 20,
    padding: 18,
    width: '100%',
    maxWidth: 360,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  closeButtonAndroid: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  iosHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    paddingRight: 32,
  },
  iosHeaderText: {
    flex: 1,
  },
  iosIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    marginRight: 12,
  },
  iosTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#fff',
  },
  iosSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.65)',
    marginTop: 2,
  },
  iosStepsWrapper: {
    gap: 12,
  },
  iosStepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  iosStepIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(70, 166, 255, 0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iosStep: {
    fontSize: 13,
    color: '#f5f7ff',
    lineHeight: 18,
    flex: 1,
  },
  iosStepNumber: {
    fontWeight: '700',
    color: '#46a6ff',
  },
});

export default InstallPWAButton;

